import React, { memo, useCallback, useContext, useMemo } from "react";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// helpers
import { getDeliveryInstructionCroutonData, getDeliveryInstructionData } from "@cart-client/helpers/Cart";
import { isEmpty } from "@swiggy-private/js-utils";
import { scrollElementIntoViewWithOffset } from "@swiggy-private/core-ui";
import { DELIVERY_SECTION_ELEMENT, isPopFlow, isToingFlow } from "@swiggy-private/checkout-core";
import { getDeliverySectionComms } from "@cart-client/components/delivery-section-renderer/helpers";

// constants
import { CART_ELEMENT_IDS } from "@cart-client/components/cart-renderer/constants";

// components
import { CartDeliveryInstruction } from "@cart-client/components/cart-delivery-instruction";
import { DeliverySectionCallout } from "../DeliverySectionCallout";

// hooks
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";

// contexts
import { DeliverySectionContext } from "@cart-client/contexts/DeliverySectionContext";
import { Wrapper, InstructionsCalloutWrapper } from "./styles";

interface DeliveryInstructionsProps {
    setCroutonVisibility: (croutonVisibility: boolean) => void;
    canShowDeliveryInstructionCrouton: boolean;
    cartTopOffset: number;
}

function DeliveryInstructions({
    setCroutonVisibility,
    canShowDeliveryInstructionCrouton,
    cartTopOffset,
}: DeliveryInstructionsProps) {
    const {
        cartState: { reviewedCart: cartResponse },
    } = useContext(CartContext);

    const { setDeliveryTab } = useContext(DeliverySectionContext);

    const { onImpressionDeliveryInstructionGrouping, onImpressionDeliveryInstructionGroupingActual } =
        useCartRendererAnalytics(cartResponse);
    const isPopCart = isPopFlow();
    const isToingCart = isToingFlow();
    const deliveryInstructionArray = useMemo(
        () => getDeliveryInstructionData(cartResponse, isPopCart, isToingCart),
        [cartResponse, isPopCart, isToingCart],
    );

    const deliveryInstructionCroutonDetail = useMemo(
        () => getDeliveryInstructionCroutonData(cartResponse),
        [cartResponse],
    );

    const deliveryInstructionsTitle = useMemo(
        () => getDeliverySectionComms(cartResponse)?.delivery_instructions_title,
        [cartResponse],
    );

    const scrollToDeliveryInstructionContainer = useCallback(() => {
        // set delivery tab to instructions
        setDeliveryTab(DELIVERY_SECTION_ELEMENT.INSTRUCTIONS);

        scrollElementIntoViewWithOffset(
            CART_ELEMENT_IDS.DELIVERY_INSTRUCTIONS_CONTAINER_SELECTOR,
            cartTopOffset,
            true,
            CART_ELEMENT_IDS.CART_RENDERER_CONTAINER_SELECTOR,
        );
    }, [cartTopOffset, setDeliveryTab]);

    if (isEmpty(deliveryInstructionArray)) {
        return null;
    }

    return (
        <Wrapper isPopCart={isPopCart}>
            {!isEmpty(deliveryInstructionsTitle) && (
                <InstructionsCalloutWrapper>
                    <DeliverySectionCallout message={deliveryInstructionsTitle} />
                </InstructionsCalloutWrapper>
            )}
            <CartDeliveryInstruction
                instructionArray={deliveryInstructionArray}
                deliveryInstructionCroutonDetail={deliveryInstructionCroutonDetail}
                canShowCrouton={canShowDeliveryInstructionCrouton}
                scrollToDIContainer={scrollToDeliveryInstructionContainer}
                setCroutonVisibility={setCroutonVisibility}
                onImpressionDeliveryInstructionGrouping={onImpressionDeliveryInstructionGrouping}
                onImpressionDeliveryInstructionGroupingActual={onImpressionDeliveryInstructionGroupingActual}
            />
        </Wrapper>
    );
}

export default memo(DeliveryInstructions);
